import { useEffect } from 'react';
import { ArrowRightIcon, MapPinIcon, PhoneIcon } from 'lucide-react';
import { business, images, routes } from '../data/site';
import { SiteLink } from '../components/SiteLink';
const TITLE = 'Certified Pre-Owned Hot Tubs | Pegasus Spas';
const DESCRIPTION = 'Certified pre-owned hot tubs at Pegasus Spas in Brooklyn, Michigan. Call or stop in to see what we have right now.';
const CANONICAL = 'https://www.pegasus-spas.com/hot-tubs/certified-pre-owned';
const compare = [{
  title: 'Its make and model',
  copy: 'Ask us about the make, model and features of the spa so you can understand what it offers.'
}, {
  title: 'What comes with it',
  copy: 'Find out whether it has a cover, steps or anything else, and what you might want to add.'
}, {
  title: 'How it fits your family',
  copy: 'Seating, size and layout matter more than the price tag. Sit in it if you can.'
}, {
  title: 'Getting it home',
  copy: 'Tell us about the spot and the path to it, and we\u2019ll help plan the delivery.'
}];
const relatedLinks = [{
  label: 'Available Spas',
  href: routes.availableSpas
}, {
  label: 'New Hot Tubs',
  href: routes.hotTubs
}, {
  label: 'Financing',
  href: routes.financing
}, {
  label: 'Spa Care',
  href: routes.spaCare
}];
function upsertMeta(name: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}
export function CertifiedPreOwnedPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = TITLE;
    upsertMeta('description', DESCRIPTION);
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', CANONICAL);
  }, []);
  return <main data-id="element-889" data-mp="62njetvy9hbm">
      {/* Intro band */}
      <section aria-labelledby="preowned-h1" className="relative w-full" data-id="element-890" data-mp="166w21wkr6zl">
        <div className="relative w-full overflow-hidden min-h-[400px] sm:min-h-[380px] lg:h-[420px]" data-id="element-891" data-mp="13udpnmwmum0">
          <img src={images.pathHotTubs} alt="Hot tub with clear water on a residential wood deck surrounded by trees in Michigan" className="absolute inset-0 h-full w-full object-cover [object-position:50%_58%]" width={1600} height={1200} decoding="async" data-id="element-892" data-mp="avgo5hrf4pnp" />
          <div className="hero-scrim-compact absolute inset-0 sm:hidden" aria-hidden="true" data-id="element-893" data-mp="3sd18evwqbxvz" />
          <div className="hero-scrim absolute inset-0 hidden sm:block" aria-hidden="true" data-id="element-894" data-mp="3nclv24cg6qe0" />

          <div className="relative mx-auto flex min-h-[400px] max-w-container flex-col justify-center px-5 py-10 sm:min-h-[380px] sm:px-8 lg:h-[420px] lg:min-h-0" data-id="element-895" data-mp="17ky2sm8dpm1j">
            <p className="text-[14px] font-medium uppercase text-white sm:text-[15px]" data-id="element-896" data-mp="12p0iy5xwpxk0">
              Pre-Owned Hot Tubs
            </p>
            <h1 id="preowned-h1" className="mt-3 max-w-3xl text-h1-mobile font-semibold text-white lg:text-[48px] lg:leading-[56px]" data-id="element-897" data-mp="1f5ieeauomkfl">
              Certified Pre-Owned Hot Tubs
            </h1>
            <p className="mt-4 max-w-2xl text-body text-white sm:text-body-lg" data-id="element-898" data-mp="164u15id44o3r">
              A pre-owned spa can be a great way to start. What we have changes from month to
              month, so give us a call or stop in and we&rsquo;ll show you what&rsquo;s here right
              now.
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-899" data-mp="1k28uuzaefi1j">
              <a href={business.phoneHref} className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-btn bg-pegasus-magenta px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-900" data-mp="1p24ygxf6p7gk">
                <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-901" data-mp="syz3rbk8x5nk" />
                Call or Text {business.phone}
              </a>
              <SiteLink to={routes.location} className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-btn border-2 border-white bg-white/10 px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-white hover:text-ink" data-id="element-902" data-mp="ov19be5v8j0u">
                <MapPinIcon className="h-4 w-4" aria-hidden="true" data-id="element-903" data-mp="17mtz6zpn1o4c" />
                Visit the Showroom
              </SiteLink>
            </div>
          </div>
        </div>
      </section>

      {/* Compare */}
      <section aria-labelledby="compare-heading" className="w-full bg-white" data-id="element-904" data-mp="xxaerpma1m7d">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-16" data-id="element-905" data-mp="xv6d7ebuoza9">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14" data-id="element-906" data-mp="1vdpp1raja98y">
            <div className="lg:col-span-4" data-id="element-907" data-mp="1qkwkb3f2f0wv">
              <h2 id="compare-heading" className="font-display text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-908" data-mp="1xwuetlmu4x3k">
                What to Talk Through With Us
              </h2>
              <p className="mt-4 text-body text-ink/80 sm:text-body-lg" data-id="element-909" data-mp="27uqyx2xr32dt">
                You don&rsquo;t need to have this figured out. These are just the things
                we&rsquo;ll cover together.
              </p>
            </div>

            <dl className="grid grid-cols-1 gap-x-10 sm:grid-cols-2 lg:col-span-8" data-id="element-910" data-mp="2gvf4raczpwfm">
              {compare.map(item => <div key={item.title} className="border-t border-ink/15 py-5" data-id="element-911" data-mp="2qli3zsapg6me">
                  <dt className="text-[19px] font-semibold leading-7 text-ink" data-id="element-912" data-mp="2qnl4j69hhi5d">{item.title}</dt>
                  <dd className="mt-1.5 text-[16.5px] leading-7 text-ink/75" data-id="element-913" data-mp="2vmnz1wru85z3">{item.copy}</dd>
                </div>)}
            </dl>
          </div>
        </div>
      </section>

      {/* Related links */}
      <section aria-label="Related pages" className="w-full border-t border-ink/10 bg-white" data-id="element-914" data-mp="15gj357cxwxw0">
        <div className="mx-auto max-w-container px-5 py-6 sm:px-8" data-id="element-915" data-mp="15e58gzm69noz">
          <ul className="flex flex-col gap-x-8 gap-y-1 sm:flex-row sm:flex-wrap sm:items-center" data-id="element-916" data-mp="10izrmf7mmz7r">
            {relatedLinks.map(link => <li key={link.label} data-id="element-917" data-mp="19id1h8znxpt0">
                <SiteLink to={link.href} className="inline-flex min-h-[44px] items-center gap-1.5 text-[16px] font-medium text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-918" data-mp="19h1vr3mnq7z9">
                  {link.label}
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-919" data-mp="1t5u7eyti1aew" />
                </SiteLink>
              </li>)}
          </ul>
        </div>
      </section>
    </main>;
}